import * as z from 'zod/v4';

import { drawNumberSchema, drawStateSchema } from '../draw/entry.js';
import { apiErrorSchema } from '../error/entry.js';
import { dateTimeSchema } from '../frameworks/entry.js';
import { productSchema } from '../product/entry.js';
import type { DateDrawsResultDate } from './dateDrawsResultDate.js';

const dateDrawsResponseDateSchema = z.object({
  closeDate: dateTimeSchema,
  date: dateTimeSchema,
  drawNumber: drawNumberSchema,
  drawState: z.string(),
  drawStateId: z.number(),
  openDate: dateTimeSchema.optional().nullish(),
  product: productSchema,
});
type DateDrawsResponseDate = z.infer<typeof dateDrawsResponseDateSchema>;

const getLatestDateObjByStateOrder = (dateResults: DateDrawsResponseDate[]): string | undefined => {
  const mappedDateResults = dateResults.map((resultDate) => ({
    date: resultDate.date,
    drawState: drawStateSchema.parse(resultDate.drawStateId),
  }));

  let latestResults = mappedDateResults.filter((resultDate) => resultDate.drawState.client === 'Live');

  // If no live results, get finalized results
  if (latestResults.length === 0) {
    latestResults = mappedDateResults.filter((resultDate) => resultDate.drawState.client === 'Finished');
  }

  // If no finalized results, get any available results
  if (latestResults.length === 0) {
    latestResults = mappedDateResults;
  }

  if (latestResults.length === 0) {
    return undefined;
  }

  const latestDate = latestResults.at(-1)?.date;

  return latestDate ? new Date(latestDate).toLocaleDateString('sv-SE', { timeZone: 'Europe/Stockholm' }) : undefined;
};

/**
 * Composed Zod schema
 * Used to parse the response from the API into a Result date entity
 */
export const dateDrawsResponseSchema = z
  .object({
    resultDates: z.array(dateDrawsResponseDateSchema),
  })
  .transform(({ resultDates }) => {
    // This object will result in the DateDraws typescript type
    const dateDraws = resultDates.reduce<Record<string, DateDrawsResultDate[]>>((acc, dateObject) => {
      const key = new Date(dateObject.date).toLocaleDateString('sv-SE', { timeZone: 'Europe/Stockholm' });

      acc[key] = acc[key] ?? [];
      acc[key].push({
        closeDate: dateObject.closeDate,
        date: dateObject.date,
        drawNumber: dateObject.drawNumber,
        drawState: drawStateSchema.parse(dateObject.drawStateId),
        openDate: dateObject.openDate,
        product: dateObject.product,
      });

      return acc;
    }, {});

    const latestDate = getLatestDateObjByStateOrder(resultDates);

    return { data: { results: dateDraws, latestDate } };
  });

export const dateDrawsApiResponse = {
  parse: (data: unknown) => {
    return z.union([dateDrawsResponseSchema, apiErrorSchema]).parse(data);
  },
} as const;

export type DateDrawsApiResponse = z.infer<typeof dateDrawsResponseSchema>;
